// Authenticated animal-photo loading for list/overview screens.
//
// Parity problem: acc renders each animal's locally-uploaded photo (the Jmix `DierFotoController`
// servlet, reachable from an <img> because it rides the session cookie same-origin). The SPA's photo
// bytes come from `GET /api/animals/{id}/photo/content`, which is bearer-authenticated — and a plain
// <img src> can't carry the in-memory bearer. The overview feeds only expose `fotoUrl` (the *online*
// image URL), which is null for shelter-uploaded animals, so every card fell back to the camera
// placeholder. This hook closes that gap the same way the detail hero does (src/api/animalPhoto.ts):
// fetch the bytes with the bearer, wrap the Blob in an object URL, and hand that to the <img>.
import { useEffect, useState } from "react";

import { fetchAnimalPhotoBlob } from "./animalPhoto";

// Module-level cache: animalId -> object URL, or null once we know the animal has no photo (404).
// Object URLs are intentionally long-lived: photos are small (~15 KB) and the working set is bounded to
// the animals actually browsed, so caching avoids a refetch on every re-render / pagination step and,
// crucially, never revokes a URL that is still bound to a mounted <img>.
const cache = new Map<string, string | null>();
const inflight = new Map<string, Promise<string | null>>();

// A search can return hundreds of rows, and every card wants its photo. Firing them all at once fills
// the browser's handful of connections to the host, so the very next call the operator is waiting for —
// the chosen animal's detail — queued behind ~250 image requests and took seconds to arrive. These are
// decorative: they wait their turn, a few at a time, and never crowd out the data.
const MAX_PARALLEL = 4;
let running = 0;
const queue: Array<() => void> = [];

function acquire(): Promise<void> {
  if (running < MAX_PARALLEL) {
    running += 1;
    return Promise.resolve();
  }
  return new Promise<void>((resolve) => queue.push(resolve));
}

function release(): void {
  const next = queue.shift();
  if (next) {
    next();
    return;
  }
  running -= 1;
}

function load(id: string): Promise<string | null> {
  const cached = cache.get(id);
  if (cached !== undefined) return Promise.resolve(cached);
  const existing = inflight.get(id);
  if (existing) return existing;
  const p = acquire()
    .then(() => fetchAnimalPhotoBlob(id))
    .then((blob) => {
      const url = URL.createObjectURL(blob);
      cache.set(id, url);
      return url;
    })
    .catch(() => {
      // 404 (no uploaded photo) or any error → remember "no photo" so we don't refetch; card shows the
      // placeholder exactly as before.
      cache.set(id, null);
      return null;
    })
    .finally(() => {
      inflight.delete(id);
      release();
    });
  inflight.set(id, p);
  return p;
}

/**
 * Resolve authenticated animal photos to `blob:` object URLs for a list of animal ids. Returns a map of
 * `id → url` containing only the animals that have a photo; ids without one (or still loading) are absent,
 * so callers fall back to their placeholder. Already-cached photos paint on the first render (no flash).
 */
export function useAnimalPhotoUrls(ids: Array<number | string>): Record<string, string> {
  const key = ids.join(",");
  const [urls, setUrls] = useState<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;
    const unique = Array.from(new Set(ids.map(String)));

    // Seed synchronously from the cache so photos already loaded (e.g. paging back) show immediately.
    const seeded: Record<string, string> = {};
    for (const id of unique) {
      const c = cache.get(id);
      if (c) seeded[id] = c;
    }
    setUrls(seeded);

    Promise.all(unique.map((id) => load(id).then((url) => [id, url] as const))).then((pairs) => {
      if (cancelled) return;
      const next: Record<string, string> = {};
      for (const [id, url] of pairs) if (url) next[id] = url;
      setUrls(next);
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return urls;
}
